// src/components/WelcomeStrip.js
//
// Chunk 8 — Welcome strip for new users.
//
// Soft 3-step "guide of welcome" that lives at the top of This Month.
// Steps:
//   1. Add your income            — inline form (name, amount, frequency)
//   2. Set spending limits        — sends to Plan
//   3. Log your first transaction — opens QuickAdd sheet
//
// Detection of done is automatic from the data (not from a flag):
//   1 → ≥1 income source for current month
//   2 → ≥1 budget_categories row with limit_amount > 0 in current month's budget
//   3 → ≥1 transaction in entire history
//
// Strip is hidden when profile.welcome_dismissed_at is set. We set that
// when the user clicks "Skip welcome" OR all 3 steps are done.
//
import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { theme, firstName } from '../lib/theme'
import { useCurrency } from '../lib/CurrencyContext'
import QuickAddTransaction from './QuickAddTransaction'

const FREQ_OPTIONS = [
  { value: 'monthly',  label: 'Monthly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'annual',   label: 'Annually' },
]

export default function WelcomeStrip({ profile, hasIncome, hasPlan, hasTransactions, onChange }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(null) // null | 'income' | 'transaction'
  const [dismissing, setDismissing] = useState(false)

  const completed = [hasIncome, hasPlan, hasTransactions].filter(Boolean).length
  const total = 3
  const allDone = completed === total

  // When all 3 done, auto-dismiss after a short victory pause.
  // This is the "fades out and disappears" promise.
  useEffect(() => {
    if (!allDone) return
    const t = setTimeout(() => handleDismiss(true), 2000)
    return () => clearTimeout(t)
  }, [allDone]) // eslint-disable-line

  const handleDismiss = async (silent = false) => {
    if (dismissing) return
    if (!silent && !window.confirm('Skip the welcome guide? You can always finish setup from the menu.')) return
    setDismissing(true)
    if (profile?.id) {
      await supabase.from('profiles')
        .update({ welcome_dismissed_at: new Date().toISOString() })
        .eq('id', profile.id)
    }
    if (onChange) onChange()
  }

  const meName = firstName(profile?.full_name) || 'there'

  return (
    <div style={{ ...styles.wrap, ...(allDone ? styles.wrapDone : {}) }}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerKicker}>
            <span>Welcome to Merq Vault</span>
          </div>
          <div style={styles.headerTitle}>
            {allDone
              ? `You're all set, ${meName}.`
              : `Let's get you set up, ${meName}.`}
          </div>
          <div style={styles.headerSub}>
            {allDone
              ? "Tucking this guide away — you'll see your real dashboard in a moment."
              : `${completed} of ${total} done · takes about 2 minutes`}
          </div>
        </div>
        {!allDone && (
          <button
            type="button" className="btn-compact"
            style={styles.skipBtn}
            onClick={() => handleDismiss(false)}
            disabled={dismissing}
            aria-label="Skip welcome"
          >
            Skip
          </button>
        )}
      </div>

      <div style={styles.cardRow}>
        {/* Step 1 — Income */}
        <StepCard
          step={1}
          done={hasIncome}
          title="Add your income"
          sub="Tell Merq Vault what's coming in each month."
          ctaLabel={hasIncome ? 'Done' : 'Add income'}
          onClick={() => {
            if (hasIncome) return
            setExpanded(expanded === 'income' ? null : 'income')
          }}
          expanded={expanded === 'income'}
          expandedContent={
            expanded === 'income' && (
              <IncomeForm
                profileId={profile?.id}
                onSaved={() => {
                  setExpanded(null)
                  if (onChange) onChange()
                }}
                onCancel={() => setExpanded(null)}
              />
            )
          }
        />

        {/* Step 2 — Plan */}
        <StepCard
          step={2}
          done={hasPlan}
          title="Set spending limits"
          sub="Pick a few categories with monthly limits."
          ctaLabel={hasPlan ? 'Done' : 'Open plan'}
          onClick={() => {
            if (hasPlan) return
            navigate('/plan')
          }}
        />

        {/* Step 3 — Transaction */}
        <StepCard
          step={3}
          done={hasTransactions}
          title="Log a transaction"
          sub="Try the daily habit — should take 10 seconds."
          ctaLabel={hasTransactions ? 'Done' : 'Add one'}
          onClick={() => {
            if (hasTransactions) return
            setExpanded(expanded === 'transaction' ? null : 'transaction')
          }}
        />
      </div>

      {/* QuickAdd opened from card 3 */}
      {expanded === 'transaction' && (
        <QuickAddTransaction
          onClose={() => setExpanded(null)}
          onSaved={() => {
            setExpanded(null)
            if (onChange) onChange()
          }}
        />
      )}
    </div>
  )
}

// ─── Step card ──────────────────────────────────────────────

function StepCard({ step, done, title, sub, ctaLabel, onClick, expanded, expandedContent }) {
  return (
    <div
      style={{
        ...styles.card,
        ...(done ? styles.cardDone : {}),
        ...(expanded ? styles.cardExpanded : {}),
      }}
    >
      <div style={styles.cardTop}>
        <div style={styles.cardStepNum}>
          {done ? '✓' : step}
        </div>
      </div>
      <div style={styles.cardTitle}>{title}</div>
      <div style={styles.cardSub}>{sub}</div>
      <button
        type="button"
        style={{
          ...styles.cardCta,
          ...(done ? styles.cardCtaDone : {}),
        }}
        onClick={onClick}
        disabled={done}
      >
        {ctaLabel}
      </button>
      {expandedContent}
    </div>
  )
}

// ─── Inline income form (Step 1) ────────────────────────────

function IncomeForm({ profileId, onSaved, onCancel }) {
  const { formatCurrency } = useCurrency()
  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')
  const [frequency, setFrequency] = useState('monthly')
  const [saving, setSaving] = useState(false)

  const monthlyEquiv = useMemo(() => {
    const n = parseFloat(amount)
    if (!n || isNaN(n)) return 0
    if (frequency === 'weekly')   return n * 4.33
    if (frequency === 'biweekly') return n * 2.17
    if (frequency === 'annual')   return n / 12
    return n
  }, [amount, frequency])

  const handleSave = async () => {
    if (saving) return
    if (!name.trim() || !amount) return
    setSaving(true)
    const today = new Date()
    const month = today.getMonth() + 1
    const year = today.getFullYear()
    await supabase.from('income_sources').insert({
      user_id: profileId,
      name: name.trim(),
      amount: parseFloat(amount),
      frequency,
      month, year,
      is_active: true,
    })
    setSaving(false)
    onSaved()
  }

  return (
    <div style={styles.inlineForm}>
      <input
        type="text"
        placeholder="e.g. Salary, Freelance"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={styles.inlineInput}
        aria-label="Income name"
        autoFocus
      />
      <div style={styles.inlineRow}>
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{ ...styles.inlineInput, flex: 1 }}
          aria-label="Amount"
        />
        <select
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          style={styles.inlineSelect}
          aria-label="Frequency"
        >
          {FREQ_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {monthlyEquiv > 0 && frequency !== 'monthly' && (
        <div style={styles.monthlyEquiv}>
          ≈ {formatCurrency(monthlyEquiv)} per month
        </div>
      )}
      <div style={styles.inlineActions}>
        <button
          type="button" className="btn-compact"
          style={styles.inlineCancel}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          style={{
            ...styles.inlineSave,
            opacity: (!name.trim() || !amount || saving) ? 0.5 : 1,
            cursor: (!name.trim() || !amount || saving) ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSave}
          disabled={!name.trim() || !amount || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  wrap: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '20px',
    padding: '20px 22px 22px',
    marginBottom: '24px',
    boxShadow: theme.shadows.card,
    transition: 'opacity 400ms ease, transform 400ms ease',
    fontFamily: f.body,
  },
  wrapDone: {
    opacity: 0.7,
    transform: 'scale(0.985)',
  },

  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', gap: '12px',
    marginBottom: '18px',
  },
  headerLeft: { flex: 1, minWidth: 0 },
  headerKicker: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontSize: '11.5px', fontWeight: 600,
    color: c.green,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    marginBottom: '6px',
  },
  headerTitle: {
    fontFamily: f.display, fontSize: '24px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.025em',
    marginBottom: '4px',
  },
  headerSub: {
    fontSize: '13px', color: c.inkMuted,
  },
  skipBtn: {
    background: 'transparent', color: c.inkMuted,
    border: 'none', padding: '6px 10px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600,
    cursor: 'pointer', borderRadius: '6px',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    flexShrink: 0,
  },

  cardRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  },

  card: {
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '8px',
    transition: 'all 200ms ease',
    minWidth: 0,
  },
  cardDone: {
    background: c.greenSoft,
    borderColor: 'rgba(22, 163, 74, 0.25)',
  },
  cardExpanded: {
    gridColumn: '1 / -1',
    background: c.white,
    borderColor: c.lineStrong,
  },
  cardTop: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '8px',
  },
  cardIcon: {
    width: '34px', height: '34px',
    borderRadius: '10px',
    background: c.white,
    border: `1px solid ${c.line}`,
    color: c.plumSoft,
    display: 'grid', placeItems: 'center',
  },
  cardIconDone: {
    background: c.green, color: '#FFFFFF',
    borderColor: c.green,
  },
  cardStepNum: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '18px', color: c.inkQuiet,
    letterSpacing: '-0.015em',
  },
  cardTitle: {
    fontSize: '14.5px', fontWeight: 600,
    color: c.plum,
    marginTop: '4px',
  },
  cardSub: {
    fontSize: '12.5px', color: c.inkMuted,
    lineHeight: 1.45, flex: 1,
    marginBottom: '4px',
  },
  cardCta: {
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '8px',
    padding: '8px 14px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '5px',
    width: 'fit-content',
  },
  cardCtaDone: {
    background: 'transparent', color: c.green,
    cursor: 'default',
  },

  // Inline income form
  inlineForm: {
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: `1px solid ${c.line}`,
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  inlineInput: {
    width: '100%',
    padding: '10px 12px',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, outline: 'none',
    boxSizing: 'border-box',
  },
  inlineRow: {
    display: 'flex', gap: '8px',
  },
  inlineSelect: {
    width: '160px',
    padding: '10px 12px',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  monthlyEquiv: {
    fontSize: '12px', color: c.green,
    fontWeight: 600, fontStyle: 'italic',
  },
  inlineActions: {
    display: 'flex', gap: '8px', marginTop: '4px',
  },
  inlineCancel: {
    flex: 1,
    padding: '10px',
    background: 'transparent', color: c.plumSoft,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer',
  },
  inlineSave: {
    flex: 2,
    padding: '10px',
    background: c.green, color: '#FFFFFF',
    border: 'none', borderRadius: '8px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px',
  },
}
